import React, { useState, useEffect, useRef } from "react";
import {
    Flex, Icon, IconButton, Input, InputGroup, InputLeftElement, Text, Tooltip, useColorModeValue,
} from "@chakra-ui/react";
import { MdSearch, MdRefresh, MdAssignment } from "react-icons/md";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Header section with a brand icon badge, title, and refresh button.
 * @param {Object} props
 * @param {boolean} props.refreshing - Whether a refresh is in progress
 * @param {Function} props.onRefresh - Callback to trigger refresh
 * @returns {React.ReactElement}
 */
function ListHeader({ refreshing, onRefresh }) {
    const badgeBg = useColorModeValue("brand.100", "whiteAlpha.100");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="center" justify="space-between" mb="16px">
            <Flex align="center" gap="12px">
                <Flex align="center" justify="center" w="44px" h="44px" borderRadius="12px" bg={badgeBg}>
                    <Icon as={MdAssignment} w="24px" h="24px" color="brand.500" />
                </Flex>
                <Text fontSize="lg" fontWeight="700" color={titleColor}>Demandes de certification</Text>
            </Flex>
            <Tooltip label="Rafraîchir la liste" placement="top" hasArrow>
                <IconButton aria-label="Rafraîchir la liste" size="sm" variant="ghost"
                    isLoading={refreshing} onClick={onRefresh} icon={<Icon as={MdRefresh} />} />
            </Tooltip>
        </Flex>
    );
}

/**
 * Resyncs the local input with the parent searchTerm when it changes externally
 * (e.g. filter reset). Avoids overwriting in-progress typing.
 * @param {string} searchTerm - Parent-controlled term
 * @param {{current:string}} lastEmittedRef - Ref of last value emitted to parent
 * @param {Function} setLocalValue - Local input setter
 */
function syncSearchFromParent(searchTerm, lastEmittedRef, setLocalValue) {
    if (searchTerm !== lastEmittedRef.current) {
        lastEmittedRef.current = searchTerm || "";
        setLocalValue(searchTerm || "");
    }
}

/**
 * Schedules a debounced emit of the current input value to the parent.
 * Returns a cleanup that cancels the pending timer.
 * @param {string} localValue - Current input value
 * @param {{current:string}} lastEmittedRef - Ref of last value emitted
 * @param {Function} onSearchChange - Parent callback
 * @returns {Function|undefined} Cleanup function
 */
function debounceSearch(localValue, lastEmittedRef, onSearchChange) {
    if (localValue === lastEmittedRef.current) return undefined;
    const timer = setTimeout(() => {
        lastEmittedRef.current = localValue;
        onSearchChange(localValue);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
}

/**
 * Search input field with 300ms debounce. Keeps a local input state so typing is
 * instant, then propagates the term up to the parent after the debounce delay.
 * @param {Object} props
 * @param {string} props.searchTerm - Committed search term from parent state
 * @param {Function} props.onSearchChange - Callback fired with the debounced term
 * @returns {React.ReactElement}
 */
function SearchBar({ searchTerm, onSearchChange }) {
    const [localValue, setLocalValue] = useState(searchTerm || "");
    const lastEmittedRef = useRef(searchTerm || "");
    const iconColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");

    useEffect(() => { syncSearchFromParent(searchTerm, lastEmittedRef, setLocalValue); }, [searchTerm]);
    useEffect(() => debounceSearch(localValue, lastEmittedRef, onSearchChange), [localValue, onSearchChange]);

    return (
        <InputGroup>
            <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color={iconColor} />
            </InputLeftElement>
            <Input value={localValue} placeholder="Rechercher par nom ou email..."
                onChange={(e) => setLocalValue(e.target.value)} borderRadius="12px" />
        </InputGroup>
    );
}

/**
 * Compact footer showing the total count of certifications on a single line.
 * @param {Object} props
 * @param {number} props.count - Number of certifications
 * @returns {React.ReactElement}
 */
function ListFooter({ count }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Flex align="center" justify="flex-end" gap="6px" mt="12px" px="4px">
            <Text color={valueColor} fontWeight="700" fontSize="sm">{count}</Text>
            <Text color={labelColor} fontSize="sm">demande{count > 1 ? "s" : ""} au total</Text>
        </Flex>
    );
}

export { ListHeader, SearchBar, ListFooter };
